import {
  Color,
  Mesh,
  OrthographicCamera,
  PlaneBufferGeometry,
  RGBAFormat,
  Scene,
  ShaderMaterial,
  Vector2,
  WebGLRenderTarget,
} from "three";
import vs from "../glsl/isoline.vert";
import fs from "../glsl/isoline.frag";
import cvs from "../glsl/composite.vert";
import cfs from "../glsl/composite.frag";
import { clamp, norm } from "../utils/utils";
let geom, isoline, quad, composite;
let _thumbnails, _edges, _renderer, _camera, _scene;
let rtScene, rtCamera, rt0, rt1, rt2;

export default class Isoline {
  constructor(thumbnails, edges, camera, scene, renderer) {
    _thumbnails = thumbnails;
    _edges = edges;
    _camera = camera;
    _scene = scene;
    _renderer = renderer;

    //fullscreen plane with custom material
    geom = new PlaneBufferGeometry(1, 1, 1, 1);
    isoline = new ShaderMaterial({
      uniforms: {
        map: { value: null },
        bubbles: { value: null },
        alpha: { value: 1 },
        from: { value: 0.48 },
        to: { value: 0.5 },
        feather: { value: 0.025 },
        color: { value: new Color() },
      },
      vertexShader: vs,
      fragmentShader: fs,
    });
    composite = new ShaderMaterial({
      uniforms: {
        background: { value: null },
        foreground: { value: null },
      },
      vertexShader: cvs,
      fragmentShader: cfs,
      transparent: true,
    });

    quad = new Mesh(geom, isoline);

    //camera and scene to render the fullscreen quad
    let s = 0.5;
    rtCamera = new OrthographicCamera(-s, s, s, -s, 0.01, 2);
    rtCamera.position.z = 1;
    rtScene = new Scene();
    rtScene.add(quad);

    //render targets
    let size = new Vector2();
    renderer.getSize(size);
    rt0 = new WebGLRenderTarget(size.x, size.y, { format: RGBAFormat });
    rt1 = new WebGLRenderTarget(size.x, size.y, { format: RGBAFormat });
    rt2 = new WebGLRenderTarget(size.x, size.y, { format: RGBAFormat });
  }

  resize(w, h) {
    rt0.setSize(w, h);
    rt0.needsUpdate = true;
    rt1.setSize(w, h);
    rt1.needsUpdate = true;
    rt2.setSize(w, h);
    rt2.needsUpdate = true;
  }
  getCameraRatio(min = 3, max = 5, k = 4) {
    const x = clamp(0, norm(_camera.position.z, min, max), 1);
    const a = 0.5 * Math.pow(2.0 * (x < 0.5 ? x : 1.0 - x), k);
    return clamp(0, x < 0.5 ? a : 1.0 - a, 1);
  }

  renderScene(rt) {
    _renderer.setRenderTarget(rt);
    _renderer.render(_scene, _camera);
    _renderer.setRenderTarget(null);
  }
  renderRTScene(rt) {
    _renderer.setRenderTarget(rt);
    _renderer.render(rtScene, rtCamera);
    _renderer.setRenderTarget(null);
  }

  render(cluster) {
    //get a "zoom" value to set the size of the central bubble (default = 1)
    const zoom = (1 - this.getCameraRatio(16, 6, 2)) * 2.5;

    //alpha hides the isoline at a given distance
    const alpha = this.getCameraRatio(23, 15, 2);

    //compute the isolines:

    //hide edges to prevent bleeding
    _edges.visible = false;

    //render the bubbles to texture rt0
    _thumbnails.setBubblesMaterial(cluster.uid, zoom);
    this.renderScene(rt0);

    // compute the isolines
    quad.material = isoline;
    isoline.uniforms.bubbles.value = rt0.texture;
    isoline.uniforms.color.value = new Color(cluster.color);
    isoline.uniforms.feather.value = 0.0125;
    isoline.uniforms.alpha.value = alpha;
    this.renderRTScene(rt2);

    //the result of the isolones is now in texture rt2

    //displays to screen:

    //render pie charts to texture rt1
    _edges.visible = true;
    _thumbnails.setPieChartsMaterial();
    this.renderScene(rt1);

    //pass both textures as uniforms
    quad.material = composite;
    composite.uniforms.background.value = rt1.texture;
    composite.uniforms.foreground.value = rt2.texture;
    _renderer.render(rtScene, rtCamera);
  }
}
